/**
 * Wave 3.56 — Approval Return dialog
 *
 * Dedicated dialog to return material from an on-approval raw-material
 * packet back to the vendor **without** converting to stock first.
 *
 * The dialog re-uses the existing backend endpoint
 *   POST /api/{material}/packets/{id}/issue
 * with ``issue_type='return_to_vendor'``. The endpoint already:
 *   • posts an ``on_approval_return`` entry to the vendor amount ledger
 *   • generates a vendor return memo number
 *   • auto-cleans the packet when the balance reaches zero
 *
 * Behaviour (confirmed with user):
 *   1) Partial returns are allowed (user may return part of the in-hand
 *      balance — it is NOT forced to be the entire packet)
 *   2) Only the packet's in-hand balance is returnable. Goldsmith-issued
 *      quantity is invisible to this flow and stays out
 *   3) UX after success: toast + stay on the packet page (the parent
 *      reloads via ``onReturned``)
 *
 * Props
 * -----
 *  - open, onOpenChange       : Dialog visibility control
 *  - material                 : 'diamond' | 'color-stone' | 'pearl'
 *  - packet                   : the on-approval packet (must have
 *                               balance_carats > 0 and balance_pieces > 0)
 *  - onReturned               : called after a successful submission
 */
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { formatCarats, formatINR } from '@/lib/format';
import { validateCaratMovement } from '@/lib/caratValidation';
import { extractError } from '@/lib/errors';
import { ArrowDownToLine } from 'lucide-react';

const MATERIAL_META = {
  diamond: { apiBase: '/diamonds', label: 'Diamond' },
  'color-stone': { apiBase: '/color-stones', label: 'Color Stone' },
  pearl: { apiBase: '/pearls', label: 'Pearl' },
};

export default function ApprovalReturnDialog({
  open,
  onOpenChange,
  material = 'diamond',
  packet,
  onReturned,
}) {
  const meta = MATERIAL_META[material] || MATERIAL_META.diamond;
  const isOnApproval = packet?.purchase_status === 'on_approval';
  const balanceCt = Number(packet?.balance_carats || 0);
  const balancePcs = Number(packet?.balance_pieces || 0);
  const defaultRate = Number(packet?.current_rate_per_ct || 0);

  const [form, setForm] = useState({
    carats: '',
    pieces: '',
    return_rate_per_ct: '',
    reference: '',
    note: '',
  });
  const [vendor, setVendor] = useState({ id: '', name: '' });
  const [resolving, setResolving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset form + resolve vendor whenever the dialog opens for a packet.
  useEffect(() => {
    if (!open || !packet) return;
    setForm({
      carats: balanceCt > 0 ? String(balanceCt) : '',
      pieces: balancePcs > 0 ? String(balancePcs) : '',
      return_rate_per_ct: '',
      reference: '',
      note: '',
    });
    setVendor({ id: '', name: '' });
    // Resolve purchase vendor from the packet's first receipt entry — an
    // on-approval packet must be returned to the vendor it was purchased
    // from. Same convention used by the Issue → Return to Vendor path.
    (async () => {
      setResolving(true);
      try {
        const { data } = await api.get(`${meta.apiBase}/packets/${packet.id}/ledger`);
        const first = (data.entries || []).find((e) => e.type === 'receipt' && e.vendor_id);
        if (first?.vendor_id) {
          setVendor({ id: first.vendor_id, name: first.vendor_name || '' });
        } else {
          setVendor({
            id: packet.vendor_id || '',
            name: packet.vendor_name || '',
          });
        }
      } catch {
        setVendor({
          id: packet.vendor_id || '',
          name: packet.vendor_name || '',
        });
      } finally {
        setResolving(false);
      }
    })();
  }, [open, packet?.id]);

  const ct = Number(form.carats) || 0;
  const pcs = Number(form.pieces) || 0;

  const previewRate = useMemo(() => {
    const custom = Number(form.return_rate_per_ct);
    return form.return_rate_per_ct && custom > 0 ? custom : defaultRate;
  }, [form.return_rate_per_ct, defaultRate]);

  const previewValue = useMemo(() => ct * previewRate, [ct, previewRate]);

  const overBalance =
    ct > balanceCt + 1e-9 || pcs > balancePcs;
  const emptyPacket = balanceCt <= 0 && balancePcs <= 0;

  const setFullBalance = () => {
    setForm((f) => ({
      ...f,
      carats: balanceCt > 0 ? String(balanceCt) : '',
      pieces: balancePcs > 0 ? String(balancePcs) : '',
    }));
  };

  const submit = async () => {
    if (!isOnApproval) {
      toast.error('Only on-approval packets can be returned via Approval Return.');
      return;
    }
    if (!vendor.id) {
      toast.error(
        'Purchase vendor for this packet could not be resolved. Approval Return is unavailable.'
      );
      return;
    }
    // Wave 3.49 Rule 1 + Rule 2 client-side validation (mirrors backend).
    const err = validateCaratMovement({
      txnCarats: ct,
      txnPieces: pcs,
      availableCarats: balanceCt,
      availablePieces: balancePcs,
      packetNo: packet?.packet_no,
      context: 'approval return',
    });
    if (err) {
      toast.error(err);
      return;
    }
    const payload = {
      type: 'issue',
      issue_type: 'return_to_vendor',
      carats: ct,
      pieces: pcs,
      vendor_id: vendor.id,
      vendor_name: vendor.name,
      reference: form.reference || '',
      note: form.note || '',
    };
    const custom = Number(form.return_rate_per_ct);
    if (form.return_rate_per_ct && custom > 0) payload.rate_per_ct = custom;

    setSubmitting(true);
    try {
      await api.post(`${meta.apiBase}/packets/${packet.id}/issue`, payload);
      toast.success('Approval return posted to vendor ledger');
      onOpenChange(false);
      if (typeof onReturned === 'function') onReturned();
    } catch (e) {
      toast.error(extractError(e, 'Approval return failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const disabled =
    submitting ||
    emptyPacket ||
    !vendor.id ||
    ct <= 0 ||
    pcs <= 0 ||
    overBalance;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (emptyPacket && v) return;
        onOpenChange(v);
      }}
    >
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="approval-return-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-800">
            <ArrowDownToLine size={18} className="text-amber-700" />
            Approval Return · {packet?.packet_no}
          </DialogTitle>
          <DialogDescription>
            Return on-approval {meta.label.toLowerCase()} stock to the original vendor
            without converting it to stock. You may return part or all of the packet&apos;s
            in-hand balance.
          </DialogDescription>
        </DialogHeader>

        {!isOnApproval ? (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            This packet is not marked <strong>On Approval</strong>. Approval Return is
            unavailable — use Purchase Return instead.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              This will post an <span className="font-semibold">on_approval_return</span>{' '}
              entry against the vendor and generate a vendor return memo. If the packet
              balance reaches zero it will be auto-closed.
            </div>

            <div>
              <Label>Vendor <span className="text-rose-600">*</span></Label>
              <Input
                value={
                  vendor.name ||
                  (resolving
                    ? 'Resolving purchase vendor…'
                    : 'Vendor could not be resolved')
                }
                readOnly
                disabled
                className="bg-slate-100 text-slate-700 cursor-not-allowed"
                data-testid="approval-return-vendor-locked"
              />
              <div className="text-[10px] text-slate-500 mt-1">
                On-approval packets must be returned to the vendor they were purchased
                from. Auto-selected from this packet&apos;s purchase record.
              </div>
            </div>

            <div className="rounded-md border border-[var(--divider)] bg-slate-50 px-3 py-2 text-xs text-slate-700">
              <div className="flex items-center justify-between">
                <span className="uppercase tracking-wider text-[10px] text-slate-500">
                  In-hand Balance
                </span>
                <button
                  type="button"
                  onClick={setFullBalance}
                  className="text-[11px] text-[var(--ink-navy)] hover:underline"
                  data-testid="approval-return-fill-full"
                >
                  Return entire balance
                </button>
              </div>
              <div className="mt-1 font-mono tabular-nums">
                {formatCarats(balanceCt)} ct · {balancePcs} pcs
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Carats to return <span className="text-rose-600">*</span></Label>
                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  max={balanceCt}
                  value={form.carats}
                  onChange={(e) => setForm({ ...form, carats: e.target.value })}
                  data-testid="approval-return-carats"
                />
              </div>
              <div>
                <Label>Pieces to return <span className="text-rose-600">*</span></Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  max={balancePcs}
                  value={form.pieces}
                  onChange={(e) => setForm({ ...form, pieces: e.target.value })}
                  data-testid="approval-return-pieces"
                />
              </div>
            </div>

            <div>
              <Label>
                Return Rate ₹/ct{' '}
                <span className="text-slate-400">(optional)</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.return_rate_per_ct}
                onChange={(e) =>
                  setForm({ ...form, return_rate_per_ct: e.target.value })
                }
                placeholder={`Defaults to purchase rate ₹${Number(defaultRate).toLocaleString('en-IN')}/ct`}
                data-testid="approval-return-rate"
              />
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label>Reference / Memo No <span className="text-slate-400">(optional)</span></Label>
                <Input
                  value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  placeholder="e.g. Vendor challan / memo reference"
                  data-testid="approval-return-reference"
                />
              </div>
              <div>
                <Label>Note <span className="text-slate-400">(optional)</span></Label>
                <Textarea
                  rows={2}
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Reason for return, condition, etc."
                  data-testid="approval-return-note"
                />
              </div>
            </div>

            <div
              className="rounded-md border border-[var(--divider)] bg-[var(--paper-warm)] px-3 py-2 text-xs text-slate-700"
              data-testid="approval-return-preview"
            >
              <div className="flex items-center justify-between">
                <span className="uppercase tracking-wider text-[10px] text-slate-500">
                  Return Preview
                </span>
                <span className="font-mono tabular-nums text-[var(--ink-navy)]">
                  {formatCarats(ct)} ct · {pcs} pcs · {formatINR(previewValue)}
                </span>
              </div>
              <div className="mt-1 text-[10px] text-slate-500">
                Rate: ₹
                {Number(previewRate).toLocaleString('en-IN', {
                  maximumFractionDigits: 2,
                })}
                /ct{form.return_rate_per_ct && Number(form.return_rate_per_ct) > 0
                  ? ' (custom)'
                  : ' (packet rate)'}
              </div>
              {overBalance && (
                <div className="mt-2 text-[11px] text-rose-700">
                  Return quantity exceeds the packet&apos;s in-hand balance.
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="approval-return-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={disabled}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="approval-return-submit"
          >
            {submitting ? 'Returning…' : 'Confirm Approval Return'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
